/* eslint-disable import/no-duplicates */
import { ptBR } from 'date-fns/locale';
import { format } from 'date-fns';
import { getMonthByNumber } from '~/utils/getMonthByNumber';
import {
  ContractExtensionProposal,
  NotExtendingReason,
} from '~/types/extensionProposal';
import { DefaultDiscount, DefaultDiscountVIew } from '~/types/discount';
import api from '~/utils/api';
import { formatCurrency } from '~/utils/formatCurrency';
import { SelectItem } from '~/types';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { ContractServices } from '~/types/contract-services';

export const parse = (valueAsString: string) => {
  let valueAsNumber = 0;
  try {
    let parsedValueAsString = valueAsString.replace(/\D/g, '');
    parsedValueAsString = parsedValueAsString.padStart(2 + 1, '0');
    parsedValueAsString = `${parsedValueAsString.substring(
      0,
      parsedValueAsString.length - 2,
    )}.${parsedValueAsString.slice(2 * -1)}`;
    valueAsNumber = parseFloat(parsedValueAsString);
  } catch {
    valueAsNumber = 0;
  }
  return valueAsNumber;
};

export const getDiscounts = async (
  query: string,
): Promise<DefaultDiscountVIew[]> => {
  const { data } = await api.get<DefaultDiscount[]>(
    '/proposta_extensao/descontos',
    {
      params: {
        contrato_id: query,
      },
    },
  );

  return data.map(discount => ({
    ...discount,
    formattedDescontoPadrao: formatCurrency(
      parse(String(discount.desconto_padrao)),
    ).replace('R$', ''),
    formattedDescontoMinimo: formatCurrency(
      parse(String(discount.desconto_minimo)),
    ).replace('R$', ''),
    formattedDescontoMaximo: formatCurrency(
      parse(String(discount.desconto_maximo)),
    ).replace('R$', ''),
  }));
};

export const getNotExtendingReasons = async (): Promise<SelectItem[]> => {
  const { data } = await api.get<NotExtendingReason[]>(
    'proposta_extensao/motivos',
  );

  return data.map(reason => ({
    value: reason.id.toString(),
    label: reason.motivo,
  }));
};

export const getExtensionProposalContract = async (
  idContrato: string,
): Promise<ContractExtensionProposal> => {
  const { data } = await api.get<ContractExtensionProposal>(
    `/contratos/${idContrato}/prepara-extensao`,
  );

  const formattedDiscounts =
    data.proposta_extensao?.descontos.map(discount => ({
      ...discount,
      formattedDescontoTotal: Number(discount.desconto_total)
        .toString()
        .replace('.', ','),
      formattedValorCalculado: discount?.valor_calculado
        ? formatCurrency(discount?.valor_calculado)
        : '',
      formattedInicioExtensao: discount.inicio_extensao
        ? parseFormattedMonthYearDate(discount.inicio_extensao)
        : '',
      formattedValidadeDesconto: discount.validade_desconto
        ? parseDate(discount.validade_desconto)
        : '',
    })) || [];

  return {
    ...data,
    discounts: formattedDiscounts,
    formattedValue: formatCurrency(data.vr_mensal_atual),
    formattedValorMensalCorrigido: data.proposta_extensao
      ?.valor_mensal_corrigido
      ? formatCurrency(data.proposta_extensao.valor_mensal_corrigido)
      : '',
    formattedDataVencimento: formatInTimeZone(
      `${data.vencimento_final}T00:00:00Z`,
      'dd/MM/yyyy',
    ),
  };
};

const parseDate = (date: string) => {
  return formatInTimeZone(`${date}T00:00:00Z`, 'dd/MM');
};

const parseFormattedMonthYearDate = (date: string) => {
  const [year, month] = date.split('-');

  const formattedMonth = getMonthByNumber(Number(month) - 1);

  return `${
    formattedMonth[0].toUpperCase() + formattedMonth.slice(1)
  } de ${year}`;
};

export const getGenerateContractExtensionToken = async (
  idProposta: number,
): Promise<any> => {
  const { data } = await api.get<any>(
    `proposta_extensao/${idProposta}/gerar_token`,
  );

  return data;
};

export const getSetContractExtensionToken = async (
  idProposta: number,
  codigo: string,
  idContrato: string,
): Promise<any> => {
  const { data } = await api.get<any>(
    `proposta_extensao/${idProposta}/verifica_token?token=${codigo}&id_contrato=${idContrato}`,
  );

  return data;
};

export const getContractExtensionServicesOptions = async (
  idProposta: number,
): Promise<ContractServices> => {
  const { data } = await api.get<ContractServices>(
    `encerramento_extensao/${idProposta}/get_opcoes_servicos/`,
  );
  return data;
};

export const getGenerateCancelContractExtensionToken = async (
  idProposta: number,
): Promise<any> => {
  const { data } = await api.get<any>(
    `encerramento_extensao/${idProposta}/gerar_token`,
  );

  return data;
};
